import { Component, Input, OnInit, inject } from "@angular/core";
import { CoreCommonModule } from "@core/common.module";
import { User } from "@core/models";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormControl,
} from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../../../environments/environment";
import { ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { Output, EventEmitter } from "@angular/core";
import { COUNTIES, County } from "@core/constants/consts";
import { AuthenticationService } from "@core/services/auth";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { serverErrorMessage } from "@core/helpers";
interface School {
  id: number;
  name: string;
  schoolRegistration: string;
}

@Component({
  standalone: true,
  imports: [CoreCommonModule],
  selector: "app-forgot-username",
  templateUrl: "./forgot-username.component.html",
  styleUrls: ["./forgot-username.component.scss"],
})
export class ForgotUsernameComponent implements OnInit {
  @Input() isMawinguRedirect?: boolean = false;
  @Input() mawinguPhoneNumber?: string;
  @Output() getUserSubscriptionDetails = new EventEmitter<User>();
  @Output() switchToSignUpForm = new EventEmitter<string>();
  @Output() skipMawingu = new EventEmitter<void>();

  counties: County[] = COUNTIES;
  schools: School[] = [];
  selectedCounty: County | null = null;
  selectedSchool: School | null = null;
  admissionNumber: string = "";
  generatedUsername: string = "";
  loadingSchools = false;
  loadingUsername = false;
  error: string = "";

  forgotUsernameForm: UntypedFormGroup;
  schoolSearchControl = new FormControl("");
  filteredSchools: School[] = [];
  showSchoolDropdown = false;
  parentForm: UntypedFormGroup;
  showParentForm = false;
  updatingParentInfo = false;
  parentFormSuccess = false;
  parentFormError: string | null = null;
  public showMawinguAdmissionForm = false;

  checkPhoneNumber = new FormControl('', [
  Validators.required,
  Validators.pattern(/^0(7|1)\d{8}$/),
  ]);
  phoneAccountState: 'idle' | 'checking' | 'found' | 'notfound' = 'idle';
  showFoundBanner = true;
  showNotFoundBanner = true;
  showSchoolNotFoundInfoBanner = true;
  loadingPhoneLookup = false;
  loggingIn = false;

  mawinguPassword = new FormControl('', Validators.required);
  showMawinguPassword = false;
  mawinguOptionalEmail = new FormControl('', Validators.email);
  

  @Output() goToRegisterEvent = new EventEmitter<void>();

  private formbuilder = inject(UntypedFormBuilder);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);
  private helperFunctions = inject(HelperFunctionsService);

  get isSchoolNotFound(): boolean {
    return this.forgotUsernameForm.get('school')?.value === -1;
 }
  ngOnInit(): void {
    // Sort counties alphabetically by name
    this.counties = this.counties.sort((a, b) => a.name.localeCompare(b.name));
    this.forgotUsernameForm = this.formbuilder.group({
      county: ["", Validators.required],
      school: ["", Validators.required],
      admission: ["", Validators.required],
    });
    this.parentForm = this.formbuilder.group({
      parentEmail: [""],
    });
    this.schoolSearchControl.valueChanges.subscribe((value) => {
      this.onSchoolInput();
    });
    this.checkPhoneNumber.valueChanges.subscribe((value) => {
      const phone = (value || '').toString().trim();
      if (phone.length === 10 && /^0(7|1)\d{8}$/.test(phone)) {
        this.lookupAccountByPhone(phone);
      } else if (this.phoneAccountState !== 'idle') {
        this.resetPhoneCheckState(false);
      }
    });
  }

  onCountyChange() {
    this.selectedSchool = null;
    this.schools = [];
    this.forgotUsernameForm.get("school")?.setValue("");
    this.schoolSearchControl.setValue("");
    this.filteredSchools = [];
    this.showSchoolDropdown = false;
    const countyId = this.forgotUsernameForm.get("county")?.value;
    if (countyId) {
      this.loadingSchools = true;
      this.http
        .get<
          School[]
        >(`${environment.apiUrl}/check-account?countyId=${countyId}`)
        .subscribe({
          next: (schools) => {
            schools = schools.sort((a, b) => a.name.localeCompare(b.name));
            const specialEntry: School = {
              id: -1,
              name: "I Can't Find My School.",
              schoolRegistration: "notfound",
            };
            this.schools = [specialEntry, ...schools];
            this.filteredSchools = this.schools.slice();
            this.loadingSchools = false;
          },
          error: () => {
            this.schools = [];
            this.filteredSchools = [];
            this.loadingSchools = false;
          },
        });
    }
  }

  onSchoolInput() {
    const value = this.schoolSearchControl.value?.toLowerCase() || "";
    if (value) {
      this.filteredSchools = this.schools.filter((school) =>
        school.name.toLowerCase().includes(value),
      );
    } else {
      this.filteredSchools = this.schools.slice();
    }
    this.showSchoolDropdown = true;
    // Clear selection if input doesn't match
    const match = this.schools.find((s) => s.name.toLowerCase() === value);
    if (!match) {
      this.forgotUsernameForm.get("school")?.setValue("");
    }
  }

  selectSchool(school: School) {
    this.schoolSearchControl.setValue(school.name);
    this.forgotUsernameForm.get("school")?.setValue(school.id);
    this.selectedSchool = school;
    this.showSchoolDropdown = false;
  }

  get hasSelectedSchool() {
    return this.forgotUsernameForm.get("school")?.value;
  }

  onSchoolBlur() {
    setTimeout(() => {
      this.showSchoolDropdown = false;
    }, 200);
  }

  getPhoneNumberLabel(): string {
    return this.isMawinguRedirect
      ? "Provide Phone Number"
      : "Please provide your parent's Phone Number *";
  }

  fetchUsername() {
    this.error = "";
    const schoolId = this.forgotUsernameForm.get("school")?.value;
    const countyId = this.forgotUsernameForm.get("county")?.value;
    const admission = this.forgotUsernameForm.get("admission")?.value;
    if (schoolId && countyId && admission) {
      this.loadingUsername = true;
      this.http
        .get<{
          admission: string;
        }>(`${environment.apiUrl}/check-account?schoolId=${schoolId}&admission=${admission}&countyId=${countyId}`)
        .subscribe({
          next: (res) => {
            if ((res as any).message) {
              this.generatedUsername = "";
              this.error = (res as any).message;
              this.loadingUsername = false;
              this.cdr.detectChanges();
              return;
            }
            this.generatedUsername = res.admission;
            this.error = "";
            this.loadingUsername = false;
            this.showParentForm = true;
          },
          error: (err) => {
            this.generatedUsername = "";
            this.error = err?.error?.message || "An error occurred. Please try again.";
            this.loadingUsername = false;
            this.cdr.detectChanges();
          },
        });
    }
  }

  switchForm() {
    this.switchToSignUpForm.emit("register");
  }

  onParentFormSubmit() {
    if (this.parentForm.invalid || !this.generatedUsername) return;
    this.parentFormSuccess = false;
    this.parentFormError = null;
    this.updatingParentInfo = true;
    const payload = {
      username: this.generatedUsername,
      email: this.parentForm.value.parentEmail,
    };
    this.http
      .put(`${environment.apiUrl}/user/update-email`, payload)
      .subscribe({
        next: () => {
          this.parentFormSuccess = true;
          if (this.isMawinguRedirect && this.mawinguPhoneNumber) {
            this.loginUser(this.generatedUsername, this.mawinguPhoneNumber);
          } else {
            // pass username when redirecting to login
            setTimeout(() => {
              this.updatingParentInfo = false;
              this.router.navigate(["/login"], {
                queryParams: { username: this.generatedUsername },
              });
            }, 1500);
          }
        },
        error: (err) => {
          this.updatingParentInfo = false;
          this.parentFormError = serverErrorMessage(
            err,
            "Couldn't update your info. Please try again."
          );
        },
      });
  }

  loginUser(username: string, mawinguPhoneNumber: string) {
    const payloadData = {
      username: username,
      mawinguAccount: mawinguPhoneNumber,
    };
    this.authenticationService.mawinguUserLogin(payloadData).subscribe(
      (usr) => {
        this.loggingIn = false;
        const isStudent = usr.role === 30 || usr.role === 31;
        if (isStudent) {
          this.getUserSubscriptionDetails.emit(usr);
          return;
        }
        this.helperFunctions.navigateToUsersDashboard(usr);
      },
      (err) => {
        this.loggingIn = false;
        this.helperFunctions.showWarningToastr(
          serverErrorMessage(err, "Couldn't sign you in. Please try again."),
        );
      },
    );
  }

  copyUsername() {
    if (this.generatedUsername) {
      navigator.clipboard.writeText(this.generatedUsername);
      this.helperFunctions.showSucessToastr("Username copied to clipboard!");
    }
  }


  goToRegister() {
    this.goToRegisterEvent.emit();
  }

  skipToLogin(): void {
    this.skipMawingu.emit();
}

onAdmissionBlur() {
  const admission = this.forgotUsernameForm.get('admission')?.value;
  if (
    this.isMawinguRedirect && 
    admission &&
    admission.toString().trim().length > 0 &&
    !this.generatedUsername
    ) {
    this.fetchUsername();
  }
}
getSelectedCountyName(): string {
  const code = this.forgotUsernameForm.get('county')?.value;
  return this.counties.find(c => String(c.code) === String(code))?.name ?? '';
}

  getSelectedSchoolName(): string {
    return this.selectedSchool?.name ?? '';
  }

 dismissFoundBanner() {
  this.showFoundBanner = false;
}

  dismissNotFoundBanner() {
  this.showNotFoundBanner = false;
}

dismissSchoolNotFoundInfoBanner() {
  this.showSchoolNotFoundInfoBanner = false;
}

lookupAccountByPhone(phone: string) {
  this.phoneAccountState = 'checking';
  this.loadingPhoneLookup = true;
  this.showFoundBanner = true;
  this.showNotFoundBanner = true;

  this.authenticationService.checkUsernameOrPhone(phone).subscribe({
    next: (res: any) => {
       this.loadingPhoneLookup = false;
       this.phoneAccountState = 'found';
      this.cdr.detectChanges();
    },
    error: (err) => {
      this.loadingPhoneLookup = false;
      // The server answers an unknown number with a 404, or a 400 saying "not found"; any other
      // failure (a 403, a 5xx, no connection) says nothing about the account, so show why instead.
      const reason = serverErrorMessage(err, "");
      const notFound = err?.status === 404 || (err?.status === 400 && /not found/i.test(reason));
      if (notFound) {
        this.phoneAccountState = 'notfound';
      } else {
        this.phoneAccountState = 'idle';
        this.helperFunctions.showWarningToastr(
          serverErrorMessage(err, "Couldn't check that phone number. Please try again.")
        );
      }
      this.cdr.detectChanges();
    },
  });
}

resetPhoneCheckState(clearPhone: boolean = true) {
  this.phoneAccountState = 'idle';
  this.showFoundBanner = true;
  this.showNotFoundBanner = true;
  this.mawinguPassword.setValue('');
  this.mawinguPassword.markAsUntouched();
  this.mawinguOptionalEmail.setValue('');
  if (clearPhone) {
    this.checkPhoneNumber.setValue('');
  }
}

proceedToLoginWithPhone() {
  if (this.phoneAccountState !== 'found') return;
  this.loggingIn = true;
  this.loginUser('', this.checkPhoneNumber.value as string);
}

goToForgotPassword() {
  this.router.navigate(['/forgot-password']);
}
}

